import { Component, Input, OnChanges, SimpleChange, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Util, UserInterface } from '../utils/utils.module';
import { BaseDesc, ListData } from '../models/base';
import { LocalizeService } from '../services/localize.service';
import { ListItem, ListState, ListInterface } from '../models/list-item';
import { ListService } from '../services/list.service';
import { DataService } from '../services/data.service';
import { SecurityControl, AccessLevel, AccessRights } from '../models/security-control';
import { SortOrder } from '../services/schema.service';

@Component({
  providers: [ListService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
  standalone: false
})
export class ListBaseComponent extends ListInterface implements OnChanges {
  public static baseClassInputs: string[] = ['desc', 'params', 'pageSizeIncremental'];
  public ui: UserInterface;
  public isAddin: boolean;
  public reloading = true;
  public allItemsIn = false;
  public paginationStart: number;
  public paginationMax: number;
  protected set: any = {};
  protected list: ListItem[] = [];
  protected ascending: string = null;
  protected descending: string = null;
  protected uploadingList: ListItem[] = [];
  protected loadMore: string = null;
  protected security: SecurityControl = null;
  protected bIsPaged = false;
  protected schemaForm: string;
  @Input() desc?: BaseDesc;
  @Input() params?: string = null;
  @Input() pageSizeIncremental?: number = 0;

  constructor(protected listService: ListService, protected dataService: DataService, protected localizer: LocalizeService, protected changeRef: ChangeDetectorRef) {
    super();
    this.ui = Util.Device.ui;
    this.isAddin = Util.Device.bIsAddin;
    this.loadMore = this.localizer.getTranslation('PAGINATOR.LOAD_MORE');
  }

  ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {
    const chng: any = changes['desc'];
    if (chng) {
      this.reloading = true;
      if (!!this.desc && (this.desc as any).params !== 'security') {
        this.set = this.getInitialSet() || {};
        this.list = this.getInitialList() || [];
        if (this.list.length && !this.set.total) {
          this.set.total = this.list.length;
        }
        this.security = null;
        this.setSorting();
      }
      setTimeout(() => {
        if (!this.list || !this.list.length) {
          const lastListState = this.dataService.getListState(this.desc);
          if (!!lastListState && !this.params) {
            const start: number = this.bIsPaged ? lastListState.start : 0;
            let max: number = Util.RestAPI.getDefualtMaxItems();
            if (!this.bIsPaged && lastListState.start) {
              max += lastListState.start;
            }
            this.ascending = lastListState.ascending;
            this.descending = lastListState.descending;
            this.loadList(true, start, max);
          } else if (!!this.desc && !this.desc.id && (this.desc as any).params === 'security') {
            this.list = [];
            this.set = { total: 0 };
            this.reloading = false;
            this.allItemsIn = true;
          } else {
            this.loadList();
          }
        } else {
          this.reloading = false;
          this.allItemsIn = true;
        }
        this.changeRef.markForCheck();
      }, 200);
    }
  }

  protected setSorting(resetToDefault: boolean = false): void {
    if (this.params === 'history' || this.desc.type === 'activities') {
      this.setSortParams(null, 'START_DATE');
    } else if (this.desc.type === 'flexfolders') {
      this.setSortParams('DOCNAME', null);
    } else if (this.desc.type === 'folders') {
      if (this.desc.id === 'recentedits') {
        this.setSortParams(null, '%RECENTACTIVITYDATE');
      } else if (this.desc.id === 'public' || this.params === 'references' || this.params === 'associations') {
        this.setSortParams('DOCNAME', null);
      } else if (this.params === 'security') {
        this.setSortParams('USER_ID', null);
      } else {
        this.setSortParams(null, 'LAST_EDIT_DATE');
      }
    } else if (this.desc.type === 'workspaces') {
      if (this.params === 'security') {
        this.setSortParams('USER_ID', null);
      } else {
        this.setSortParams('DOCNAME', null);
      }
    } else if (this.desc.type === 'searches') {
      //Fetch the searches with most recent at the top
      this.SetSortParamsForSearch(resetToDefault);
    } else if (this.desc.type === 'fileplans') {
      //setting sort order for filepart contents.
      if ((!!this.desc['NodeLevel'] && this.desc['NodeLevel'] === 'File Part') || (Util.isRMFilePart(this.desc))) {
        this.setSortParams(null, 'LAST_EDIT_DATE');
      } else {
        //For terms,fileplans,setting the sort order to "docname" which is mapped to displayname in UI.
        this.setSortParams('DOCNAME', null);
      }
    } else if (this.desc.type === 'requests') {
      this.setSortParams(null, 'RQ.PD_REQUEST_DATE');
    } else if (!Util.isExternalLib(this.desc.lib) && !this.ascending && !this.descending) {
      this.setSortParams(null, 'LAST_EDIT_DATE');
    }
  }

  protected SetSortParamsForSearch(resetToDefault: boolean = false): void {
    const loginReply = Util.RestAPI.getLoginReply();
    const isMetaDataSearch = this.listService.isMetaDataSearch();
    if (Util.isSavedSearch(this.desc) && this.desc && this.desc['UNSAVED'] !== 'Y' && !resetToDefault) {
      const colSettings = this.listService.getSavedSearchColumnSettingsForForm(this.schemaForm);
      if (!colSettings['sortAscending'] && !colSettings['sortDescending']) {
        this.setSortParams(null, (loginReply.SEARCH_CAPABILITIES && loginReply.SEARCH_CAPABILITIES.SUMMARY === 'Y' && !isMetaDataSearch) ? 'relevance' : 'LAST_EDIT_DATE');
      } else {
        this.setSortParams(colSettings['sortAscending'], colSettings['sortDescending']);
      }
    } else {
      this.setSortParams(null, !this.desc?.id ? 'SYSTEM_ID' : (loginReply.SEARCH_CAPABILITIES && loginReply.SEARCH_CAPABILITIES.SUMMARY === 'Y' && !isMetaDataSearch) ? 'relevance' : 'LAST_EDIT_DATE');
    }
  }

  protected getInitialList(): ListItem[] {
    return null;
  }

  protected getInitialSet(): any {
    return null;
  }

  protected handleError(error: any): void {
    Util.Notify.warning(this.localizer.getTranslation('GENERIC_ERRORS.LIST_NO_LOAD'), error);
    console.log('can not load list: ' + this.desc.type + ' ' + this.desc.id + ' ' + (error.message || error));
    this.reloading = false;
    this.list = [];
    this.listReplaced();
    this.changeRef.markForCheck();
  }

  protected setSortParams(ascending: string, descending: string) {
    this.ascending = ascending || null;
    this.descending = descending || null;
  }

  private loadListPromise(desc: any, params: string, forceReload: boolean = false, ascending?: string, descending?: string, start: number = 0, max?: number, postData?: any, initialList?: any): Promise<ListItem[]> {
    const loadingDesc: any = Object.assign(this.desc);
    this.reloading = true;
    if (!forceReload && this.pageSizeIncremental > 0) {
      start = this.list.length || 0;
      max = this.pageSizeIncremental;
    }
    this.changeRef.markForCheck();
    return this.listService.getListData(loadingDesc, this.params, forceReload, ascending, descending, start, max, postData, initialList).then((data) => {
      // check for race condition that our desc has not changed after we requested the load from the list service
      // this can happen on a slow link and an impatient user that cancels a list load by going back to the previous folder
      // eg: in public folders click folder Sub to nav to and load it, then go back to public folders before the loading of Sub returns
      // Public will then request and load before the loading of sub returns and the wrong or mixed contents may be seen
      if (this.desc && this.desc.id === loadingDesc.id && this.desc.lib === loadingDesc.lib && this.desc.type === loadingDesc.type) {
        this.set = data.set;
        if (!this.listService.canIgnoreSearchLimitWarning() && this.desc.type === 'searches' && this.set?.['warning'] && this.set?.['warning_code'] === '0X40230') {
          Util.Notify.info(this.localizer.getTranslation('HEADER.SEARCH_RESULTS'), this.localizer.getTranslation('GENERIC_ERRORS.SEARCH_LIMIT_EXCEEDED', [Util.RestAPI.getSearchMaxRows()]), null, null, null, true);
        }
        if (this.set.rights) {
          if ((this.set.rights & AccessRights.ACCESS_EDIT_CONTENT) && this.set.readonly === 'Y') {
            this.set.rights = this.set.rights & ~AccessRights.ACCESS_EDIT_CONTENT;
          }
          this.security = new SecurityControl(this.set.rights);
        }
        if (!forceReload && this.pageSizeIncremental > 0) {
          this.list = this.list ? this.list.concat(data.list) : data.list;
        } else {
          this.list = data.list;
        }
        this.allItemsIn = this.list.length >= this.set.total;
        if (!!(this.desc as any).DOCNAME && (this.params === 'versions' || this.params === 'attachments')) {
          for (const item of this.list) {
            if (!item.DOCNAME) {
              item.DOCNAME = (this.desc as any).DOCNAME;
            } else {
              break;
            }
          }
        }
      }
      this.changeRef.markForCheck();
      return this.list;
    }).catch((error) => {
      this.handleError(error);
      return this.list;
    });
  }

  public loadList(forceReload: boolean = false, start?: number, max?: number, postData?: any, initilaList?: any): void {
    this.setTileSorting().then(() => {
      this.loadListPromise(this.desc, this.params, forceReload, this.ascending, this.descending, start, max, postData, initilaList).then(list => {
        this.listReplaced();
      });
    });
  }

  public loadListContent(data: ListData): void {
    if (data) {
      this.list = data.list;
      this.set = data.set;
      if (!!this.set.total || this.set.total === 0) {
        this.allItemsIn = this.list.length >= this.set.total;
      } else {
        this.allItemsIn = true;
      }
      this.listReplaced();
    }
  }

  public clearList(): void {
    this.listService.clearListData();
    this.set = {};
    this.list = [];
    this.listReplaced();
  }

  public reloadList(startFromBeginning?: boolean): void {
    this.set.total = 0;
    this.list = [];
    const paginationStart = (Util.Device.isMobile() || startFromBeginning) ? 0 : (!!this.listService ? this.listService.start : this.paginationStart);
    this.loadList(true, paginationStart, this.paginationMax);
  }

  public fetchMoreItems(): void {
    this.loadList(false);
  }

  public applyFilters(filters: any): void {
    let filterArgs = '';
    const keys: string[] = Object.keys(filters);
    for (const key of keys) {
      filterArgs += key + '=' + filters[key] + ' and ';
    }
    if (filterArgs.length) {
      filterArgs = filterArgs.substr(0, filterArgs.length - 5);  // remove last and
      this.listService.filter = filterArgs;
      this.reloadList();
    }
  }

  public spinnerOn(): void {
    this.reloading = true;
    this.changeRef.markForCheck();
  }

  public spinnerOff(): void {
    this.reloading = false;
    this.changeRef.markForCheck();
  }

  public listReplaced(): void {
    this.reloading = false;
  }

  public isReloading(): boolean {
    return this.reloading;
  }

  // if no security is available (i.e. we are in a pseudo-container) open all rights.
  public containerRights(): SecurityControl {
    let rc = this.security;
    if (!this.reloading && this.security === null) {
      rc = new SecurityControl(AccessLevel.ACCESS_LEVEL_FULL_RM);
    }
    return rc;
  }

  public getListTotal(): number {
    let total = 0;
    if (this.set.total) {
      total = this.set.total;
    }
    return total;
  }

  public getListLength(): number {
    return !!this.list ? this.list.length : 0;
  }

  public getSet(): any {
    return this.set;
  }

  public getList(): ListItem[] {
    return this.list;
  }

  public getSortKey(): string {
    return !!this.ascending ? ('ascending=' + this.ascending) : !!this.descending ? ('descending=' + this.descending) : null;
  }

  public resetSortParams(): void {
    this.setSortParams(null, 'LAST_EDIT_DATE');
  }

  public getListState(): ListState {
    return new ListState(this.ascending, this.descending, this.set.start || 0);
  }

  public listItemChanged(data: any): void {
    if (data && data.set && data.list && data.list.length === 1) {
      const newItem: ListItem = new ListItem(data.list[0]);
      const curItem: ListItem = this.list.find(i => i.id === newItem.id && i.type === newItem.type && i.lib === newItem.lib);
      if (curItem) {
        this.reloadList();
      }
    }
  }

  public canOpenListItem(listItem: ListItem): boolean {
    if (listItem && !!listItem.type && listItem.type !== 'libraries' && this.desc && this.desc.type !== 'requests' && listItem['ITEM_TYPE'] !== 'M' && !(!!listItem['PD_PART_STATUS'] && listItem['PD_PART_STATUS'].startsWith('Destroy'))) {
      if (Util.isContainer(listItem.type)) {
        return true;
      } else {
        if ((listItem['STATUS'] !== '19' && listItem['READ_ONLY'] !== 'Y' && !(listItem['checkout'] && listItem['checkout']['TYPIST_ID'].toUpperCase() !== Util.RestAPI.getUserID())) || Util.Device.bIsCordova || Util.Device.bIsElectron || Util.RestAPI.hasPFTA()) {
          const itemRights: SecurityControl = new SecurityControl(AccessLevel.ACCESS_LEVEL_FULL);
          if (listItem['%SECURITY']) {
            const itemAccess: number = +listItem['%SECURITY'];
            if (itemAccess > 0) {
              itemRights.access &= itemAccess;
            }
          }
          const extAppInfo = Util.RestAPI.findExternalApp(listItem.lib);
          if (extAppInfo && !listItem['webUrl'] && !listItem['openInAppUrl']) {
            return false;
          } else {
            return itemRights.canViewDocument && itemRights.canRetrieveDocument;
          }
        }
      }
    }
    return false;
  }

  public openListItem(listItem: ListItem, version?: string, internal?: boolean, forceOpenOrView?: boolean): void {
    if (this.canOpenListItem(listItem)) {
      const isDownloads: boolean = Util.isSharedDownloads(this.desc);
      const isImports: boolean = Util.isSharedImports(this.desc);
      if (listItem['ITEM_TYPE'] === 'M' || listItem['STATUS'] === '18' || listItem['STATUS'] === '16') {
        const errorMap = {
          18: 'FORMS.LOCAL.OPEN.ERROR_OPEN_CONTENT_DELETED',
          16: 'FORMS.LOCAL.OPEN.ERROR_OPEN_BEING_ARCHIVED'
        };
        const notifyBody: string = !!errorMap[listItem['STATUS']] ? this.localizer.getTranslation(errorMap[listItem['STATUS']], [listItem.id, listItem['DOCNAME']]) : this.localizer.getTranslation('FORMS.LOCAL.OPEN.ERROR_OPEN_PAPER');
        Util.Notify.warning(this.localizer.getTranslation('FOLDER_ACTIONS.OPEN'), notifyBody);
      } else {
        if ((isDownloads || isImports) && (forceOpenOrView || Util.isUnmangedFile(listItem))) {
          const fullPath: string = listItem['fullPath'];
          if (fullPath && fullPath.toUpperCase().endsWith('.DRF')) {
            Util.RestAPI.openDRFItem(listItem);
          } else if (forceOpenOrView) {
            if (listItem['ver']) {
              version = 'version_label' + listItem['ver'];
            } else if (!version) {
              version = 'C';
            }
            Util.RestAPI.viewFile(listItem, version, null, null, internal);
          }
          // can not preview an unmanaged file so do nothing
        } else if (listItem.type === 'urls' || Util.isContainer(listItem.type) || (Util.isExternalLib(listItem.lib) && !!(listItem as any).url)) {
          this.listService.openItem(listItem);
        } else {
          const extAppInfo = Util.RestAPI.findExternalApp(listItem.lib);
          if (Util.Device.isMobile() && !forceOpenOrView) {
            // do not call base class as it will download files we always want preview
            this.listService.openMetadata(this.list, this.set, this.containerRights(), listItem, this.listService.getProfileTab(listItem));
          } else if (extAppInfo) {
            if (listItem['openInAppUrl']) {
              const openWindow: any = Util.Help.openURL(listItem['openInAppUrl']);
              if (Util.Device.bIsElectron) {
                setTimeout(() => {
                  Util.Help.closeURL(openWindow);
                }, 3000);
              } else {
                listItem['openWindow'] = openWindow;
              }
            } else if (listItem['webUrl']) {
              const openTab: any = Util.Help.openURL(listItem['webUrl']);
              listItem['openWindow'] = openTab;
            }
          } else {
            Util.RestAPI.viewOrOpenFilesWithStatusCheck([listItem], !!version ? [version] : undefined, internal, null);
          }
        }
      }
    }
  }

  public listItemClicked(listItem: ListItem, event: Event, internal?: boolean, forceOpenOrView?: boolean): void {
    this.openListItem(listItem, null, internal, forceOpenOrView);
  }

  public checkOutAndOpenFile(listItem: ListItem, version: string, path: string): void {
    Util.RestAPI.checkoutDocument(listItem, version).then(success => {
      if (success) {
        Util.RestAPI.viewFile(listItem, version, null, path, false, true);
      }
    });
  }

  public uploadFiles(items: any[], intoThisFolder?: ListItem): void {
    const files: File[] = typeof items[0] !== 'string' ? items : null;
    const filePaths: string[] = typeof items[0] === 'string' ? items : null;
    Util.RestAPI.uploadFilesWithUI(files, filePaths, null, intoThisFolder ? intoThisFolder : this.desc);
  }

  protected async setTileSorting(): Promise<void> {
    if (this.ui >= 2 || !this.desc || !Util.isSortConfigEnabledTile(this.desc)) {
      return;
    }
    const sortInfo: any = await Util.RestAPI.getAppComponent()?.getTileSortInfo(this.desc.id, this.desc.type, this.desc.lib);
    if (!sortInfo || !sortInfo.sortColumn || !(sortInfo.sortColumnOrder in SortOrder)) {
      return;
    }
    if (sortInfo.sortColumnOrder === SortOrder.DESC) {
      this.setSortParams(null, sortInfo.sortColumn);
    } else {
      this.setSortParams(sortInfo.sortColumn, null);
    }
  }
}
